import { useCallback, useEffect, useRef, useState } from "react";
import { normalizeSearchInput } from "../utils/normalizeSearchInput";

export const SEARCH_DEBOUNCE_MS = 2500;

export function useDebouncedValue<T>(value: T, delay = SEARCH_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export function useSearchInput(
  controlledValue?: string,
  onSearchChange?: (value: string) => void,
) {
  const isControlled = controlledValue !== undefined;
  const [input, setInput] = useState(controlledValue ?? "");
  const debounced = useDebouncedValue(input);
  const skipSyncRef = useRef(false);
  const skipMountRef = useRef(true);
  const lastEmittedRef = useRef<string | null>(null);
  const onSearchChangeRef = useRef(onSearchChange);

  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    if (!isControlled || skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    setInput(controlledValue ?? "");
  }, [controlledValue, isControlled]);

  useEffect(() => {
    if (!onSearchChangeRef.current) return;

    if (skipMountRef.current) {
      skipMountRef.current = false;
      lastEmittedRef.current = debounced;
      return;
    }

    if (lastEmittedRef.current === debounced) return;

    lastEmittedRef.current = debounced;
    skipSyncRef.current = true;
    onSearchChangeRef.current(debounced);
  }, [debounced]);

  const onChange = useCallback((value: string) => {
    setInput(normalizeSearchInput(value));
  }, []);

  const clear = useCallback(() => {
    setInput("");
    skipSyncRef.current = true;
    lastEmittedRef.current = "";
    onSearchChangeRef.current?.("");
  }, []);

  return { input, debounced, onChange, clear, setInput };
}
